import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createSyncEngine } from '../sync';
import { createHeartbeatEngine } from '../heartbeat';
import { hydrateManifest, getLastGoodManifest, type HydrationProgress } from '../hydrate';
import { getLastManifestHash } from '../storage';
import { Fallback } from './Fallback';
import { Loop } from './Loop';
import { DebugOverlay } from './DebugOverlay';
import type { Manifest, ManifestItem } from '../types';
import { isRenderableItem } from '../templates';

interface PlayerProps {
  token: string;
  onUnauthorized: () => void;
}

interface ActiveState {
  manifest: Manifest;
  // M2E.D: items the player knows how to render right now. MEDIA items
  // always count; MENU_BOARD/PROMO count iff their templateKey is in the
  // registry (see templates/TemplateView.tsx). Items that fail
  // isRenderableItem() are dropped here at adopt time so Loop never
  // receives them.
  renderableItems: ManifestItem[];
  index: number;
}

interface PlayerStats {
  lastSyncAt: number | null;
  lastSyncResult: 'OK' | '304' | 'ERROR' | null;
  lastSyncError: string | null;
  errorCount: number;
  hydrationProgress: HydrationProgress | null;
  lastHeartbeatAt: number | null;
  lastHeartbeatResult: 'OK' | 'ERROR' | null;
}

// M2E.D: compute renderable items at adopt time. Includes MEDIA items
// (always) and non-MEDIA items whose templateKey is in the template
// registry. Anything else gets dropped with a one-shot warn so unknown
// items can never reach Loop/ItemView/TemplateView and crash playback.
function getRenderableItems(manifest: Manifest): ManifestItem[] {
  const renderable: ManifestItem[] = [];
  const skippedTags: string[] = [];
  for (const item of manifest.playlist.items) {
    if (isRenderableItem(item)) {
      renderable.push(item);
      continue;
    }
    const kind = (item as { itemKind?: string }).itemKind ?? 'unknown';
    const templateKey = (item as { templateKey?: string }).templateKey;
    const tag = templateKey ? `${kind}:${templateKey}` : kind;
    if (!skippedTags.includes(tag)) skippedTags.push(tag);
  }
  if (skippedTags.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[player] Skipping items with no renderer: ${skippedTags.join(', ')}.`,
    );
  }
  return renderable;
}

function makeActive(manifest: Manifest, index: number): ActiveState {
  return { manifest, renderableItems: getRenderableItems(manifest), index };
}

export function Player({ token, onUnauthorized }: PlayerProps) {
  const [active, setActive] = useState<ActiveState | null>(null);
  const [pending, setPending] = useState<Manifest | null>(null);
  const [booting, setBooting] = useState(true);
  const [hydrating, setHydrating] = useState(false);
  // Stats are accumulated but unused in M2C.7. M2C.9 will display them.
  const [stats, setStats] = useState<PlayerStats>({
    lastSyncAt: null,
    lastSyncResult: null,
    lastSyncError: null,
    errorCount: 0,
    hydrationProgress: null,
    lastHeartbeatAt: null,
    lastHeartbeatResult: null,
  });

  // Debug overlay visibility (toggled by the 'd' key).
  const [debugVisible, setDebugVisible] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key !== 'd' || e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      setDebugVisible((v) => !v);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Ref lets sync callbacks read latest active without recreating the engine.
  const activeRef = useRef<ActiveState | null>(null);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  // Stable advance() via ref pattern — keeps ItemView's duration timer from
  // resetting every time active/pending change identity.
  const advanceRef = useRef<() => void>(() => undefined);
  useEffect(() => {
    advanceRef.current = () => {
      if (!active) return;
      const items = active.renderableItems;
      const nextIndex = active.index + 1;
      if (nextIndex >= items.length) {
        // End of playlist — swap to pending if any, else wrap to start.
        if (pending) {
          setActive(makeActive(pending, 0));
          setPending(null);
        } else {
          setActive({ ...active, index: 0 });
        }
      } else {
        setActive({ ...active, index: nextIndex });
      }
    };
  });
  const advance = useCallback(() => advanceRef.current(), []);

  // Bootstrap from IDB on mount. Lets the player resume cached content
  // instantly on browser refresh / kiosk restart without waiting for sync.
  useEffect(() => {
    void (async () => {
      try {
        const last = await getLastGoodManifest();
        if (last) setActive(makeActive(last, 0));
      } finally {
        setBooting(false);
      }
    })();
  }, []);

  // Sync engine. Polls manifest, drives hydration + swap.
  useEffect(() => {
    async function handleNewManifest(m: Manifest): Promise<void> {
      setStats((s) => ({
        ...s,
        lastSyncAt: Date.now(),
        lastSyncResult: 'OK',
        lastSyncError: null,
      }));

      // Empty / fallback manifest — adopt directly, no hydration needed.
      if (m.playlist.items.length === 0) {
        setActive(makeActive(m, 0));
        setPending(null);
        return;
      }

      setHydrating(true);
      const result = await hydrateManifest(m, {
        onProgress: (p) => setStats((s) => ({ ...s, hydrationProgress: p })),
      });
      setHydrating(false);

      if (result.ok) {
        // First content → play now. Otherwise stage for next item boundary.
        if (!activeRef.current) {
          setActive(makeActive(m, 0));
        } else {
          setPending(m);
        }
      } else {
        setStats((s) => ({
          ...s,
          errorCount: s.errorCount + 1,
          lastSyncError: `Hydration failed (${result.failed}/${result.totalItems})`,
        }));
      }
    }

    const sync = createSyncEngine(
      token,
      () => getLastManifestHash(),
      {
        onManifestUpdate: (m) => {
          void handleNewManifest(m);
        },
        onNotModified: () =>
          setStats((s) => ({ ...s, lastSyncAt: Date.now(), lastSyncResult: '304' })),
        onUnauthorized: () => onUnauthorized(),
        onError: (e) =>
          setStats((s) => ({
            ...s,
            lastSyncAt: Date.now(),
            lastSyncResult: 'ERROR',
            lastSyncError: e.message,
            errorCount: s.errorCount + 1,
          })),
      },
    );
    sync.start();
    return () => sync.stop();
  }, [token, onUnauthorized]);

  // Heartbeat engine — posts liveness + cache stats every 60s.
  useEffect(() => {
    const heartbeat = createHeartbeatEngine(token, {
      onSuccess: () =>
        setStats((s) => ({ ...s, lastHeartbeatAt: Date.now(), lastHeartbeatResult: 'OK' })),
      onUnauthorized: () => onUnauthorized(),
      onError: () =>
        setStats((s) => ({ ...s, lastHeartbeatAt: Date.now(), lastHeartbeatResult: 'ERROR' })),
    });
    heartbeat.start();
    return () => heartbeat.stop();
  }, [token, onUnauthorized]);

  // Render decision tree.
  let main: ReactNode;
  if (booting) {
    main = <Fallback message="Initializing…" />;
  } else if (!active) {
    main = <Fallback message={hydrating ? 'Loading content…' : 'Waiting for content'} />;
  } else if (active.renderableItems.length === 0) {
    // M2E.C: no renderable MEDIA items (playlist may be empty OR consist
    // entirely of MENU_BOARD/PROMO items pending render code in M2E.D).
    // Show the branded fallback rather than a black screen.
    main = <Fallback message={active.manifest.fallback.message || 'Waiting for content'} />;
  } else {
    const currentItem = active.renderableItems[active.index];
    main = currentItem
      ? <Loop item={currentItem} onAdvance={advance} />
      : <Fallback message="Waiting for content" />;
  }

  return (
    <>
      {main}
      <DebugOverlay
        visible={debugVisible}
        token={token}
        activeManifest={active?.manifest ?? null}
        currentIndex={active?.index ?? 0}
        hasPendingSwap={pending !== null}
        isHydrating={hydrating}
        stats={stats}
      />
    </>
  );
}
